import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Table,
  Input,
  Switch,
  Button,
  Space,
  Typography,
  Alert,
  Dropdown,
  Tooltip,
  Badge,
  Tag,
  message,
  type MenuProps,
} from 'antd';
import {
  ReloadOutlined,
  RobotOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  CopyOutlined,
  FilterOutlined,
  StarFilled,
  StarOutlined,
  HistoryOutlined,
  PlusOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { AgentEditorDrawer } from './AgentEditorDrawer';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useStore } from '@/stores';
import { AGENT_LIST_PAGE_SIZES } from '@/stores/AgentRegistryStore';
import type { AgentDefinition } from '@/api/types';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { SectionCard } from '@/components/SectionCard';
import { AgentFilterDrawer } from './AgentFilterDrawer';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { AgentStatusPill, AgentStatusToggle } from '@/components/agentStatus';

const { Text } = Typography;

// Real screen wired to /api/Agent/Search. Reactive to merchant context. The SERVER searches, filters, orders and pages
// (2 Nov 2026): the list used to filter the first 100 of the practice's 256 agents in the browser.

export const AgentsListPage = observer(function AgentsListPage() {
  const { agentRegistryStore: agents, merchantContextStore: ctx, themeStore } = useStore();
  const navigate = useNavigate();
  const isDark = themeStore.mode === 'dark';
  const [filterOpen, setFilterOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  // Re-fetch on every page mount. Without this the Agents page relied
  // entirely on the MobX reaction in AgentRegistryStore (fires only on
  // selectedMerchantId CHANGE), which created a confusing post-deploy
  // failure mode:
  //   1. First load right after API deploy → reaction fires → fetch hits
  //      cold App Service → request fails or arrives late → store's
  //      `error` is set, agents stay empty.
  //   2. User navigates away. The late-arriving response eventually
  //      resolves and fills the store, but the user has already left.
  //   3. User comes back → no re-fetch trigger (merchant unchanged) →
  //      they see the data the late response wrote, but the original
  //      "Failed to load" alert had already left a bad first impression.
  // Re-fetching on mount overwrites the stale error with a fresh attempt,
  // matches the AgentDetailPage convention (which also reloads on mount),
  // and is cheap (one /api/Agent/Search call returning a small
  // paged list).
  useEffect(() => {
    if (ctx.selectedMerchantId != null) {
      void agents.fetch();
      void agents.fetchShelves();
    }
    // ctx.selectedMerchantId is observable; including it as a dep means
    // we also re-run if the user switches merchant from another tab and
    // the dep changes between renders. The store's own reaction handles
    // the in-tab switch case, so the two paths are redundant by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The text the shown page was asked with (typing alone does not ask: Enter does).
  const searching = !!agents.shownSearch;

  if (ctx.selectedMerchantId == null) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<RobotOutlined />}
          title="Pick a merchant to begin"
          description="Use the merchant switcher in the top bar to scope the agent roster."
          size="lg"
        />
      </SectionCard>
    );
  }

  function openEditor(row: AgentDefinition): void {
    trackEvent('Agent.EditOpened', { agentId: row.id });
    navigate(`/agents/${row.id}`);
  }

  function moreMenu(row: AgentDefinition): MenuProps {
    return {
      items: [
        {
          key: 'view',
          icon: <EyeOutlined />,
          label: 'View overview',
          onClick: () => navigate(`/agents/${row.id}`),
        },
        {
          key: 'test',
          icon: <PlayCircleOutlined />,
          label: 'Test (coming soon)',
          disabled: true,
        },
        { type: 'divider' as const },
        {
          key: 'copy-id',
          icon: <CopyOutlined />,
          label: `Copy id #${row.id}`,
          onClick: () => {
            void navigator.clipboard?.writeText(String(row.id));
            trackEvent('Agent.IdCopied', { agentId: row.id });
          },
        },
      ],
    };
  }

  const handleTogglePin = async (row: AgentDefinition) => {
    const wasPinned = agents.pinnedAgentIds.has(row.id);
    try {
      await agents.togglePin(row.id);
      void message.success(wasPinned ? 'Unpinned' : `Pinned ${row.displayName ?? `agent #${row.id}`}`);
      trackEvent(wasPinned ? 'Agent.Unpinned' : 'Agent.Pinned', { agentId: row.id });
    } catch (err) {
      void message.error('Could not save pin: ' + (err as Error).message);
    }
  };

  const columns: ColumnsType<AgentDefinition> = [
    {
      title: '',
      key: 'pin',
      width: 40,
      align: 'center',
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
      render: (_text, row) => {
        const pinned = agents.pinnedAgentIds.has(row.id);
        return (
          <Tooltip title={pinned ? 'Unpin from your favorites' : 'Pin to your favorites'}>
            <Button
              type="text"
              size="small"
              aria-label={pinned ? 'Unpin' : 'Pin'}
              icon={
                pinned ? (
                  <StarFilled style={{ color: colors.amber[500] }} />
                ) : (
                  <StarOutlined style={{ color: isDark ? colors.slate[500] : colors.slate[400] }} />
                )
              }
              onClick={() => void handleTogglePin(row)}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'displayName',
      key: 'displayName',
      // Ordered by the server (the page holds one slice of the practice).
      sorter: true,
      sortOrder: agents.sort?.field === 'name' ? (agents.sort.descending ? 'descend' : 'ascend') : null,
      render: (text: string, row) => (
        <div>
          <div
            style={{
              color: isDark ? colors.slate[50] : colors.slate[900],
              fontWeight: 600,
              fontSize: 13.5,
              letterSpacing: '-0.005em',
            }}
          >
            {text}
          </div>
          {row.a2AAgentName && (
            <div
              style={{
                fontSize: 11.5,
                color: isDark ? colors.slate[400] : colors.slate[500],
                fontFamily: "'JetBrains Mono', monospace",
                marginTop: 2,
              }}
            >
              {row.a2AAgentName}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_text, row) => <AgentStatusPill agent={row} />,
    },
    {
      title: 'Audience',
      key: 'audience',
      width: 170,
      render: (_text, row) => {
        const tags: React.ReactNode[] = [];
        if (row.patientApp) tags.push(<StatusPill key="p" tone="ink">Patient</StatusPill>);
        if (row.merchantApp) tags.push(<StatusPill key="m" tone="violet">Care team</StatusPill>);
        if (!row.patientApp && !row.merchantApp)
          tags.push(<StatusPill key="n" tone="slate">—</StatusPill>);
        return <Space size={4}>{tags}</Space>;
      },
    },
    {
      title: 'Skill router',
      dataIndex: 'enableSkillRouter',
      key: 'enableSkillRouter',
      width: 130,
      render: (v: boolean | undefined) =>
        v ? <StatusPill tone="green">Enabled</StatusPill> : <StatusPill tone="slate">Disabled</StatusPill>,
    },
    {
      // CATEGORY — replaces the prior Status/Scope/ID trio. Status was
      // duplicative (already conveyed by the row-level "Inactive" pill in
      // the Audience column when it applies), Scope ("Global" vs
      // "Merchant") rarely changes during normal use, and the numeric ID
      // is available in the agent detail page when needed. Category is
      // a much higher-signal axis for triaging a 100+ agent roster.
      title: 'Category',
      key: 'category',
      width: 180,
      // Sort by category name when populated; uncategorized agents
      // sort last under "—".
      sorter: true,
      sortOrder: agents.sort?.field === 'category' ? (agents.sort.descending ? 'descend' : 'ascend') : null,
      render: (_text, row) => {
        const cat = row.category;
        if (!cat) {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Text>
          );
        }
        return (
          <Tag
            color={cat.color || 'blue'}
            icon={<AppstoreOutlined />}
            style={{ margin: 0 }}
          >
            {cat.name}
          </Tag>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      align: 'right',
      fixed: 'right',
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
      render: (_text, row) => (
        <Space size={2}>
          <Tooltip title="Open the configuration tabs">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditor(row)}
            >
              Edit
            </Button>
          </Tooltip>
          <Dropdown menu={moreMenu(row)} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" icon={<MoreOutlined />} aria-label="More actions" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="ink"
        icon={<RobotOutlined />}
        title={
          <Space size={6} align="center">
            <span>Agents</span>
            <HelpHotspot topic="agents.overview" />
          </Space>
        }
        subtitle={ctx.selectedMerchant?.title ?? '—'}
        count={agents.totalCount}
        countLabel={
          (agents.totalCount === 1 ? 'agent' : 'agents') + (agents.activeFilterCount > 0 || searching ? ' match' : '')
        }
        actions={
          <Space>
            <Input.Search
              placeholder="Search agents…"
              allowClear
              style={{ width: 260 }}
              value={agents.search}
              onChange={(e) => agents.setSearch(e.target.value)}
              onSearch={() => void agents.submitSearch()}
            />
            <AgentStatusToggle
              value={agents.statusFilter}
              onChange={(v) => {
                agents.setStatusFilter(v);
                trackEvent('AgentFilters.StatusChanged', { value: v });
              }}
            />
            <Tooltip title="Multi-criteria filter (audience, status, router)">
              <Badge
                count={agents.activeFilterCount}
                size="small"
                offset={[-4, 4]}
                color={colors.ink[600]}
              >
                <Button
                  icon={<FilterOutlined />}
                  type={agents.activeFilterCount > 0 ? 'primary' : 'default'}
                  ghost={agents.activeFilterCount > 0}
                  onClick={() => {
                    setFilterOpen(true);
                    trackEvent('AgentFilters.DrawerOpened');
                  }}
                >
                  Filter
                </Button>
              </Badge>
            </Tooltip>
            <Tooltip title="Include global (cross-merchant) agents">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  color: isDark ? colors.slate[300] : colors.slate[600],
                }}
              >
                <Switch
                  size="small"
                  checked={agents.includeGlobal}
                  onChange={(v) => {
                    agents.setIncludeGlobal(v);
                    void agents.fetch();
                  }}
                />
                Global
              </span>
            </Tooltip>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void agents.fetch()}
              loading={agents.isLoading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditorOpen(true);
                trackEvent('Agent.NewOpened');
              }}
            >
              New agent
            </Button>
          </Space>
        }
      />

      {agents.error && (
        <Alert
          type="error"
          message="Failed to load agents"
          description={agents.error}
          showIcon
          closable
        />
      )}

      {/* ⭐ Pinned — server-backed favorites. Hidden when empty. */}
      {agents.pinnedAgents.length > 0 && (
        <SectionCard
          title={
            <Space size={6}>
              <StarFilled style={{ color: colors.amber[500] }} />
              <span>Pinned</span>
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                {agents.pinnedAgents.length} favorite{agents.pinnedAgents.length === 1 ? '' : 's'}
              </Text>
            </Space>
          }
        >
          <AgentChipGrid
            agents={agents.pinnedAgents}
            isDark={isDark}
            onOpen={(row) => navigate(`/agents/${row.id}`)}
            onTogglePin={handleTogglePin}
            pinnedIds={agents.pinnedAgentIds}
          />
        </SectionCard>
      )}

      {/* 🕒 Recent — last-N agents the user opened. localStorage; per-merchant. */}
      {agents.recentAgents.length > 0 && (
        <SectionCard
          title={
            <Space size={6}>
              <HistoryOutlined style={{ color: colors.violet[500] }} />
              <span>Recently opened</span>
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                last {agents.recentAgents.length}
              </Text>
            </Space>
          }
        >
          <AgentChipGrid
            agents={agents.recentAgents}
            isDark={isDark}
            onOpen={(row) => navigate(`/agents/${row.id}`)}
            onTogglePin={handleTogglePin}
            pinnedIds={agents.pinnedAgentIds}
            compact
          />
        </SectionCard>
      )}

      <SectionCard noPadding>
        <Table<AgentDefinition>
          rowKey="id"
          columns={columns}
          dataSource={agents.agents}
          loading={agents.isLoading}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: agents.page,
            pageSize: agents.pageSize,
            total: agents.totalCount,
            showSizeChanger: true,
            pageSizeOptions: AGENT_LIST_PAGE_SIZES,
            showTotal: (t) => (agents.activeFilterCount > 0 || searching ? `${t} match` : `${t} agents`),
            style: { padding: '12px 18px', margin: 0 },
          }}
          onChange={(pagination, _filters, sorter, extra) => {
            if (extra.action === 'sort') {
              const s = Array.isArray(sorter) ? sorter[0] : sorter;
              const field = !s?.order ? null : s.columnKey === 'displayName' ? 'name' : s.columnKey === 'category' ? 'category' : null;
              void agents.setSort(field ? { field, descending: s.order === 'descend' } : null);
            } else if (extra.action === 'paginate') {
              void agents.setPage(pagination.current ?? 1, pagination.pageSize ?? agents.pageSize);
            }
          }}
          onRow={(row) => ({
            onClick: () => openEditor(row),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText:
              searching && agents.activeFilterCount === 0 ? (
                <BrandedEmpty
                  icon={<RobotOutlined />}
                  title={`No agent matches “${agents.shownSearch}”`}
                  description="The search reads the names, canonical name, description and id."
                  size="md"
                />
              ) : agents.activeFilterCount > 0 ? (
                <BrandedEmpty
                  icon={<FilterOutlined />}
                  title="No agents match these filters"
                  description="Try clearing or widening filters in the panel on the right."
                  size="md"
                  action={
                    <Button onClick={() => agents.clearFilters()}>Clear filters</Button>
                  }
                />
              ) : (
                <BrandedEmpty
                  icon={<RobotOutlined />}
                  title={`No agents in ${ctx.selectedMerchant?.title ?? 'this merchant'}`}
                  description="Try widening with the Global toggle, or switch merchants."
                  size="md"
                />
              ),
          }}
        />
      </SectionCard>

      <AgentFilterDrawer open={filterOpen} onClose={() => setFilterOpen(false)} />

      <AgentEditorDrawer
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={async (created) => {
          // Refresh the registry, then jump straight to the new agent so the
          // user can wire skills/knowledge without an extra navigation step.
          await agents.fetch();
          if (created?.id) navigate(`/agents/${created.id}`);
        }}
      />
    </div>
  );
});

// ── AgentChipGrid ───────────────────────────────────────────────────────────
//
// Compact card-grid used by both the ⭐ Pinned and 🕒 Recent sections. Each
// chip is one tappable card with the agent's display name, audience pills,
// status, and a hover-to-show pin toggle. The grid auto-wraps on narrower
// viewports — minmax(240px, 1fr) gives ~4 chips on a wide monitor and
// gracefully reflows.

function AgentChipGrid({
  agents,
  isDark,
  onOpen,
  onTogglePin,
  pinnedIds,
  compact,
}: {
  agents: AgentDefinition[];
  isDark: boolean;
  onOpen: (row: AgentDefinition) => void;
  onTogglePin: (row: AgentDefinition) => void;
  pinnedIds: Set<number>;
  compact?: boolean;
}) {
  const minWidth = compact ? 200 : 240;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`,
        gap: 10,
      }}
    >
      {agents.map((row) => (
        <AgentChip
          key={row.id}
          row={row}
          isDark={isDark}
          onOpen={onOpen}
          onTogglePin={onTogglePin}
          isPinned={pinnedIds.has(row.id)}
          compact={compact}
        />
      ))}
    </div>
  );
}

function AgentChip({
  row,
  isDark,
  onOpen,
  onTogglePin,
  isPinned,
  compact,
}: {
  row: AgentDefinition;
  isDark: boolean;
  onOpen: (row: AgentDefinition) => void;
  onTogglePin: (row: AgentDefinition) => void;
  isPinned: boolean;
  compact?: boolean;
}) {
  return (
    <div
      onClick={() => onOpen(row)}
      style={{
        cursor: 'pointer',
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 10,
        padding: compact ? '8px 10px' : '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        transition: 'border-color 120ms ease, transform 100ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = isDark ? colors.slate[500] : colors.ink[200];
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = isDark ? colors.slate[700] : colors.slate[200];
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text
          style={{
            fontWeight: 600,
            fontSize: compact ? 13 : 14,
            color: isDark ? colors.slate[50] : colors.slate[900],
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.displayName ?? `Agent #${row.id}`}
        </Text>
        <Tooltip title={isPinned ? 'Unpin' : 'Pin'}>
          <Button
            type="text"
            size="small"
            aria-label={isPinned ? 'Unpin' : 'Pin'}
            icon={
              isPinned ? (
                <StarFilled style={{ color: colors.amber[500] }} />
              ) : (
                <StarOutlined style={{ color: isDark ? colors.slate[500] : colors.slate[400] }} />
              )
            }
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin(row);
            }}
          />
        </Tooltip>
      </div>
      <Space size={4} wrap>
        {row.patientApp && <StatusPill tone="ink">Patient</StatusPill>}
        {row.merchantApp && <StatusPill tone="violet">Care team</StatusPill>}
        {!row.patientApp && !row.merchantApp && <StatusPill tone="slate">Internal</StatusPill>}
        <AgentStatusPill agent={row} />
        {!compact && row.merchantId == null && <StatusPill tone="violet">Global</StatusPill>}
      </Space>
    </div>
  );
}
